// Navbar.jsx
// This component creates the navigation bar at the top of every page

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/Navbar.css';

function Navbar() {
  // HOOKS EXPLAINED:
  
  // 1. useLanguage: Custom hook from our context to get language data
  const { toggleLanguage, t } = useLanguage();
  
  // 2. useState: Creates state for mobile menu (open/closed)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // 3. useState: Track if page is scrolled (for styling changes)
  const [isScrolled, setIsScrolled] = useState(false);
  
  // 4. useLocation: Gets current page path (from React Router)
  const location = useLocation();

  // useEffect: Add scroll event listener when component mounts
  useEffect(() => {
    const handleScroll = () => {
      // If page is scrolled more than 50px, set isScrolled to true
      setIsScrolled(window.scrollY > 50);
    };

    // Add the event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup function: Remove event listener when component unmounts
    // This prevents memory leaks
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array = run once on mount

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Function to close mobile menu (when user clicks a link)
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // JSX RETURN:
  // This is what actually gets rendered on the page
  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        {/* Logo Section */}
        <Link to="/" className="logo-container" onClick={closeMobileMenu}>
          <img 
            src="/saajLogo.png" 
            alt="Saaj Fashion Art Logo" 
            className="logo-img" 
          />
          <span className="logo">Saaj Fashion Art</span>
        </Link>

        {/* Right side: Links + Language Toggle */}
        <div className={`nav-right ${isMobileMenuOpen ? 'active' : ''}`}>
          {/* Navigation Links */}
          <ul className="nav-links">
            <li>
              <Link 
                to="/" 
                className={location.pathname === '/' ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                {t.nav.home}
              </Link>
            </li>
            <li>
              <Link 
                to="/about" 
                className={location.pathname === '/about' ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                {t.nav.about}
              </Link>
            </li>
            <li>
              <Link 
                to="/services" 
                className={location.pathname === '/services' ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                {t.nav.services}
              </Link>
            </li>
            <li>
              <Link 
                to="/gallery" 
                className={location.pathname === '/gallery' ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                {t.nav.gallery}
              </Link>
            </li>
            <li>
              <Link 
                to="/contact" 
                className={location.pathname === '/contact' ? 'active' : ''}
                onClick={closeMobileMenu}
              >
                {t.nav.contact}
              </Link>
            </li>
          </ul>

          {/* Language Toggle Button */}
          <button className="lang-toggle" onClick={toggleLanguage}>
            <span>🌐</span>
            <span>{t.nav.switchLang}</span>
          </button>
        </div>

        {/* Mobile Menu Button (Hamburger) */}
        <button 
          className="mobile-menu-btn" 
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>
    </nav>
  );
}

export default Navbar;

/*
KEY CONCEPTS IN THIS COMPONENT:

1. IMPORTS:
   - React: Core library
   - Link, useLocation: From react-router-dom for navigation
   - useLanguage: Our custom hook for translations

2. HOOKS USED:
   - useState: For mobile menu and scroll state
   - useEffect: For scroll event listener
   - useLanguage: For translations
   - useLocation: To highlight active page

3. CONDITIONAL RENDERING:
   - {isMobileMenuOpen ? 'active' : ''} - Shows/hides mobile menu
   - {t.nav.home} - Shows translated text

4. EVENT HANDLERS:
   - onClick={toggleMobileMenu} - Opens/closes mobile menu
   - onClick={toggleLanguage} - Switches language

5. PROPS:
   - className={location.pathname === '/' ? 'active' : ''}
   - Highlights the current page link
*/
